import React, { ReactNode } from "react";
import { Link } from "react-router-dom";
import companyLogo from "../../assets/images/logo.png";
import authBg from "../../assets/images/auth/auth-bg-chapel.jpg";
import s from "./AuthShell.module.scss";

interface AuthShellProps {
  title: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  showBackToLogin?: boolean;
}

/**
 * Shared visual shell for Login / Forgot Password / Change Password.
 * Modern, elegant layout styled with the #FFF4EE background theme.
 */
function AuthShell({
  title,
  subtitle,
  children,
  footer = null,
  showBackToLogin = false,
}: AuthShellProps) {
  const rootStyle = {
    ["--auth-bg-image" as string]: `url(${authBg})`,
  } as React.CSSProperties;

  return (
    <div className={s.root} style={rootStyle}>
      {/* Soft ambient glowing shapes for warmth and depth */}
      <div className={s.backgroundDecor} aria-hidden="true">
        <div className={s.blob1} />
        <div className={s.blob2} />
        <div className={s.blob3} />
      </div>

      <div className={s.stage}>
        <div className={s.panel}>
          <aside className={s.brandSide}>
            <div className={s.brandSidePhoto} aria-hidden="true" />
            <div className={s.brandSideOverlay} aria-hidden="true" />
            <div className={s.brandSideContent}>
              <img
                src={companyLogo}
                alt="LOVESTRONG Marriage"
                className={s.logo}
              />
              <div className={s.brandBadge}>LOVESTRONG MARRIAGE</div>
              <h2 className={s.brandTagline}>
                Strengthening marriages in faith and community
              </h2>
              <div className={s.brandDivider} />
              <p className={s.brandSubtext}>
                Empowering couples to build stronger, faith-filled lives together.
              </p>
            </div>
          </aside>

          <div className={s.formSide}>
            <div className={s.cardHeader}>
              <h1 className={s.title}>{title}</h1>
              {subtitle ? <p className={s.subtitle}>{subtitle}</p> : null}
            </div>

            <div className={s.cardBody}>{children}</div>

            {(footer || showBackToLogin) && (
              <div className={s.cardFooter}>
                {footer}
                {showBackToLogin ? (
                  <Link to="/login" className={s.backLink}>
                    <i className="mdi mdi-arrow-left me-1" /> Back to Login
                  </Link>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <p className={s.copyright}>
          {"\u00A9"} {new Date().getFullYear()} LOVESTRONG Marriage. All rights
          reserved.
        </p>
      </div>
    </div>
  );
}

export default AuthShell;
